import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Trash2, MessageCircle, Upload, AlertTriangle, 
  Image as ImageIcon, Video, Mic, Heart, Sparkles, Clock, Users, Info, Send, X, FolderHeart, ChevronLeft, Check, ShieldPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { handlePlanError } from '../lib/planError';
import { SimsConfirmDialog } from '../components/SimsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plumbob component
const Plumbob = ({ color = 'purple' }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className="w-6 h-6"
    animate={{ rotateY: [0, 180, 360] }}
    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
  >
    <defs>
      <linearGradient id={`plumbob-whisper-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3A5C8F" />
        <stop offset="100%" stopColor="#2B4C7E" />
      </linearGradient>
    </defs>
    <polygon points="12,2 22,12 12,22 2,12" fill={`url(#plumbob-whisper-${color})`} stroke="#1E3A63" strokeWidth="1" />
  </motion.svg>
);

export default function WhispersEnhanced() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [whispers, setWhispers] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWhisper, setSelectedWhisper] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentWhisper, setCommentWhisper] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [whisperComments, setWhisperComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newWhisper, setNewWhisper] = useState({
    title: '',
    content: '',
    type: 'text',
    recipient_id: '',
    frequency: 'once',
    max_deliveries: '',
    send_now: false,
    delivery_method: 'both', // 'email', 'inbox', 'both'
    whisper_sensitivity: 'standard', // standard | private | highly_sensitive
    stamp_crest: false
  });
  const [mediaFile, setMediaFile] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [albumPickerOpen, setAlbumPickerOpen] = useState(false);
  const [browsingAlbum, setBrowsingAlbum] = useState(null); // album being browsed for single-item pick
  const [pickSel, setPickSel] = useState({ photos: [], videos: [], audio: [] }); // multi-select indices while browsing
  // albumAttach: { mode:'album'|'item'|'selection', album_id, title, media_url, media_type, selected_*, label }
  const [albumAttach, setAlbumAttach] = useState(null);
  const [crestInfo, setCrestInfo] = useState(null); // { familyName, hasRenderedPng }

  // Auto-hide info banner after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInfoBanner(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [whispersRes, beneficiariesRes, userRes, albumsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/whispers`, { headers }),
        axios.get(`${BACKEND_URL}/api/beneficiaries`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/albums`, { headers }).catch(() => ({ data: [] }))
      ]);

      setWhispers(whispersRes.data);
      setBeneficiaries(beneficiariesRes.data.filter(b => b.status === 'accepted'));
      setUser(userRes.data);
      setAlbums(albumsRes.data || []);

      axios.get(`${BACKEND_URL}/api/family-tree`, { headers })
        .then((r) => setCrestInfo({
          familyName: r.data?.crest?.familyName || 'family',
          hasRenderedPng: !!r.data?.crest?.hasRenderedPng,
        }))
        .catch(() => setCrestInfo({ familyName: 'family', hasRenderedPng: false }));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openBrowse = (album) => {
    setBrowsingAlbum(album);
    setPickSel({ photos: [], videos: [], audio: [] });
  };

  const togglePick = (kind, idx) => {
    setPickSel((prev) => {
      const arr = prev[kind];
      const next = arr.includes(idx) ? arr.filter((i) => i !== idx) : [...arr, idx];
      return { ...prev, [kind]: next };
    });
  };

  const pickCount = pickSel.photos.length + pickSel.videos.length + pickSel.audio.length;

  const confirmSelection = () => {
    if (pickCount === 0 || !browsingAlbum) return;
    // A single photo -> attach as a clean single image; anything else -> a selection gallery.
    if (pickCount === 1 && pickSel.photos.length === 1) {
      const src = browsingAlbum.photos[pickSel.photos[0]];
      setAlbumAttach({ mode: 'item', media_url: src, media_type: 'image', label: `Photo from ${browsingAlbum.title}` });
    } else {
      setAlbumAttach({
        mode: 'selection',
        album_id: browsingAlbum.album_id,
        title: browsingAlbum.title,
        selected_photos: pickSel.photos,
        selected_videos: pickSel.videos,
        selected_audio: pickSel.audio,
        label: `${pickCount} item${pickCount > 1 ? 's' : ''} from ${browsingAlbum.title}`,
      });
    }
    setBrowsingAlbum(null);
    setAlbumPickerOpen(false);
  };

  const createWhisper = async () => {
    if (!newWhisper.title || !newWhisper.content) {
      toast.error('Title and content are required');
      return;
    }

    const token = localStorage.getItem('session_token');
    setIsCreating(true);

    try {
      const wantSendNow = !!newWhisper.send_now;
      const whisperData = {
        title: newWhisper.title,
        content: newWhisper.content,
        type: newWhisper.type,
        recipient_id: newWhisper.recipient_id || null,
        frequency: newWhisper.frequency,
        max_deliveries: newWhisper.max_deliveries ? parseInt(newWhisper.max_deliveries) : null,
        // Always create as draft first; we deliver AFTER media upload so photos/videos/voice are included.
        send_now: false,
        delivery_method: newWhisper.delivery_method,
        whisper_sensitivity: newWhisper.whisper_sensitivity,
        stamp_crest: !!newWhisper.stamp_crest
      };

      // Attach from an album: whole album, a selected subset, or a single item.
      if (albumAttach?.mode === 'album') {
        whisperData.album_id = albumAttach.album_id;
        whisperData.type = 'album';
      } else if (albumAttach?.mode === 'selection') {
        whisperData.album_id = albumAttach.album_id;
        whisperData.selected_photos = albumAttach.selected_photos || [];
        whisperData.selected_videos = albumAttach.selected_videos || [];
        whisperData.selected_audio = albumAttach.selected_audio || [];
        whisperData.type = 'album';
      } else if (albumAttach?.mode === 'item') {
        whisperData.media_url = albumAttach.media_url;
        whisperData.type = albumAttach.media_type;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/whispers`,
        whisperData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const whisperId = response.data.whisper_id;

      // Upload media file if the user picked one from their device (not from an album).
      if (mediaFile && !albumAttach && whisperId) {
        const formData = new FormData();
        formData.append('file', mediaFile);

        try {
          await axios.post(
            `${BACKEND_URL}/api/whispers/${whisperId}/media`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        } catch (mediaErr) {
          // Roll back the just-created whisper so nothing partial remains (e.g., plan-gated media)
          await axios.delete(
            `${BACKEND_URL}/api/whispers/${whisperId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});
          throw mediaErr;
        }
      }

      // Deliver now (after media is attached) if the user chose to send immediately.
      if (wantSendNow && whisperId) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/whispers/${whisperId}/send`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (sendErr) {
          toast.error('Your whisper was saved, but delivery failed. You can try sending it again.');
          loadData();
          setDialogOpen(false);
          return;
        }
      }

      toast.success(wantSendNow ? '🎉 Whisper sent!' : '🎉 Whisper saved! It will be delivered after your passing.');
      setNewWhisper({ 
        title: '', 
        content: '', 
        type: 'text', 
        recipient_id: '', 
        frequency: 'once', 
        max_deliveries: '',
        send_now: false,
        delivery_method: 'both',
        whisper_sensitivity: 'standard',
        stamp_crest: false
      });
      setMediaFile(null);
      setAlbumAttach(null);
      setBrowsingAlbum(null);
      setPickSel({ photos: [], videos: [], audio: [] });
      setAlbumPickerOpen(false);
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to create whisper:', error);
      handlePlanError(error, navigate, 'Failed to create whisper');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteWhisper = async () => {
    if (!selectedWhisper) return;
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/whispers/${selectedWhisper.whisper_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Whisper deleted');
      setDeleteDialogOpen(false);
      setSelectedWhisper(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete whisper:', error);
      toast.error('Failed to delete whisper');
    }
  };

  // Like/Unlike a whisper
  const toggleLike = async (whisperId) => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/whispers/${whisperId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setWhispers(prev => prev.map(w => {
        if (w.whisper_id === whisperId) {
          return {
            ...w,
            like_count: response.data.like_count,
            user_liked: response.data.user_liked,
            likes: response.data.user_liked 
              ? [...(w.likes || []), user?.user_id]
              : (w.likes || []).filter(id => id !== user?.user_id)
          };
        }
        return w;
      }));
      
      toast.success(response.data.action === 'liked' ? '💜 Liked!' : 'Unliked');
    } catch (error) {
      console.error('Failed to toggle like:', error);
      toast.error('Failed to like whisper');
    }
  };

  // Open comment dialog
  const openComments = async (whisper) => {
    setCommentWhisper(whisper);
    setCommentDialogOpen(true);
    setLoadingComments(true);
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/whispers/${whisper.whisper_id}/comments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWhisperComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
      setWhisperComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  // Add a comment
  const addComment = async () => {
    if (!newComment.trim() || !commentWhisper) return;
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/whispers/${commentWhisper.whisper_id}/comment`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWhisperComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      
      // Update whisper comment count
      setWhispers(prev => prev.map(w => {
        if (w.whisper_id === commentWhisper.whisper_id) {
          return {
            ...w,
            comment_count: (w.comment_count || 0) + 1,
            comments: [...(w.comments || []), response.data.comment]
          };
        }
        return w;
      }));
      
      toast.success('Comment added!');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    }
  };

  // Delete a comment
  const deleteComment = async (commentId) => {
    if (!commentWhisper) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/whispers/${commentWhisper.whisper_id}/comment/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWhisperComments(prev => prev.filter(c => c.comment_id !== commentId));
      
      // Update whisper comment count
      setWhispers(prev => prev.map(w => {
        if (w.whisper_id === commentWhisper.whisper_id) {
          return {
            ...w,
            comment_count: Math.max((w.comment_count || 1) - 1, 0),
            comments: (w.comments || []).filter(c => c.comment_id !== commentId)
          };
        }
        return w;
      }));
      
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="h-5 w-5" />;
      case 'video': return <Video className="h-5 w-5" />;
      case 'audio': return <Mic className="h-5 w-5" />;
      case 'album': return <FolderHeart className="h-5 w-5" />;
      default: return <MessageCircle className="h-5 w-5" />;
    }
  };

  const getRecipientName = (recipientId) => {
    if (!recipientId) return 'All Beneficiaries';
    const ben = beneficiaries.find(b => b.beneficiary_id === recipientId);
    return ben?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex justify-center mb-4">
            <Plumbob />
          </div>
          <p className="text-gray-600 font-medium">Loading whispers...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="whispers-page">
      {/* Subtle top accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#2B4C7E] via-[#C8A97E] to-[#C8A97E]" />

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full hover:bg-[#EBF0F5] flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">Whispers</h1>
                  <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Messages for your loved ones</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full hover:from-[#2B4C7E] hover:to-[#B3956B] shadow-lg flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" 
              data-testid="create-whisper-btn"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">Create Whisper</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Info Banner - Auto-hides after 3 seconds */}
      <AnimatePresence>
        {showInfoBanner && (
          <motion.div 
            className="max-w-7xl mx-auto px-6 py-4"
            initial={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-4 rounded-2xl bg-gradient-to-r from-[#EBF0F5] to-[#F7F1E8] border border-[#EBF0F5]">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#EBF0F5] rounded-full flex items-center justify-center flex-shrink-0">
                  <Info className="h-5 w-5 text-[#2B4C7E]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Your whispers are safe</p>
                  <p className="text-sm text-gray-600">
                    Whispers are only delivered to your beneficiaries <strong>after your passing</strong> when the Easy Passing process is triggered by your primary executor.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {whispers.length === 0 ? (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <MessageCircle className="h-12 w-12 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">No Whispers Yet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Create heartfelt messages for your loved ones. They&apos;ll receive them when the time comes.
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full shadow-lg"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Your First Whisper
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {whispers.map((whisper, index) => (
                <motion.div
                  key={whisper.whisper_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 rounded-3xl border border-gray-100 bg-white shadow-md hover:shadow-lg transition-all group">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                        whisper.type === 'image' ? 'bg-gradient-to-br from-[#3A5C8F] to-[#2B4C7E]' :
                        whisper.type === 'video' ? 'bg-gradient-to-br from-red-400 to-[#C8A97E]' :
                        whisper.type === 'audio' ? 'bg-gradient-to-br from-[#D0B384] to-[#C8A97E]' :
                        'bg-gradient-to-br from-[#3A5C8F] to-[#C8A97E]'
                      } text-white`}>
                        {getTypeIcon(whisper.type)}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedWhisper(whisper);
                          setDeleteDialogOpen(true);
                        }}
                        className="rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-bold text-gray-800">{whisper.title}</h3>
                      {whisper.media_url && ['image', 'video', 'audio'].includes(whisper.type) && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-[#EBF0F5] text-[#2B4C7E] px-2 py-0.5 text-[11px] font-medium"
                          data-testid={`whisper-media-tag-${whisper.type}`}
                          title={`Includes a ${whisper.type === 'image' ? 'photo' : whisper.type === 'video' ? 'video' : 'voice memo'}`}
                        >
                          {whisper.type === 'image' && <ImageIcon className="h-3 w-3" />}
                          {whisper.type === 'video' && <Video className="h-3 w-3" />}
                          {whisper.type === 'audio' && <Mic className="h-3 w-3" />}
                          {whisper.type === 'image' ? 'Photo' : whisper.type === 'video' ? 'Video' : 'Voice'}
                        </span>
                      )}
                      {whisper.type === 'album' && whisper.album && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-[#EBF0F5] text-[#2B4C7E] px-2 py-0.5 text-[11px] font-medium"
                          data-testid="whisper-media-tag-album"
                          title="Includes a photo album"
                        >
                          <FolderHeart className="h-3 w-3" /> Album
                        </span>
                      )}
                      {whisper.crest_url && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-medium"
                          data-testid={`whisper-crest-tag-${whisper.whisper_id}`}
                          title="Stamped with your family crest"
                        >
                          <ShieldPlus className="h-3 w-3" /> Crest
                        </span>
                      )}
                    </div>
                    {whisper.crest_url && (
                      <div className="mb-3" data-testid={`whisper-crest-${whisper.whisper_id}`}>
                        <img
                          src={`${BACKEND_URL}${whisper.crest_url}`}
                          alt="Family crest"
                          className="h-16 w-auto"
                        />
                      </div>
                    )}
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">{whisper.content}</p>

                    {/* Media / album preview */}
                    {whisper.type === 'album' && whisper.album ? (
                      <div className="mb-4" data-testid="whisper-album-preview">
                        <div className="grid grid-cols-3 gap-1 rounded-xl overflow-hidden">
                          {(whisper.album.photos || []).slice(0, 3).map((src, i) => (
                            <img key={i} src={src} alt="" className="w-full h-20 object-cover" />
                          ))}
                          {(whisper.album.photos || []).length === 0 && (
                            <div className="col-span-3 h-20 bg-[#EBF0F5] flex items-center justify-center">
                              <FolderHeart className="h-6 w-6 text-[#2B4C7E]" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {whisper.album.title} · {(whisper.album.photos || []).length} photos
                          {(whisper.album.videos || []).length ? `, ${whisper.album.videos.length} videos` : ''}
                          {(whisper.album.audio_recordings || []).length ? `, ${whisper.album.audio_recordings.length} voice` : ''}
                        </p>
                      </div>
                    ) : whisper.media_url && (
                      <div className="mb-4 rounded-xl overflow-hidden">
                        {whisper.type === 'image' && (
                          <img src={whisper.media_url} alt="Whisper media" className="w-full h-32 object-cover" />
                        )}
                        {whisper.type === 'video' && (
                          <video src={whisper.media_url} className="w-full h-32 object-cover" />
                        )}
                        {whisper.type === 'audio' && (
                          <div className="h-12 bg-gradient-to-r from-[#F2E9D9] to-[#F2E9D9] flex items-center justify-center">
                            <Mic className="h-6 w-6 text-[#B3956B]" />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Users className="h-4 w-4" />
                        <span>To: {getRecipientName(whisper.recipient_id)}</span>
                      </div>
                      {whisper.frequency && (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock className="h-4 w-4" />
                          <span className="capitalize">{whisper.frequency}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-[#EBF0F5] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Like Button */}
                        <button
                          onClick={() => toggleLike(whisper.whisper_id)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all ${
                            whisper.user_liked || (whisper.likes || []).includes(user?.user_id)
                              ? 'bg-[#F2E9D9] text-[#B3956B]' 
                              : 'hover:bg-[#F7F1E8] text-gray-400 hover:text-[#C8A97E]'
                          }`}
                        >
                          <Heart className={`h-4 w-4 ${
                            whisper.user_liked || (whisper.likes || []).includes(user?.user_id) ? 'fill-[#C8A97E]' : ''
                          }`} />
                          <span className="text-xs font-medium">
                            {whisper.like_count || (whisper.likes || []).length || 0}
                          </span>
                        </button>
                        
                        {/* Comment Button */}
                        <button
                          onClick={() => openComments(whisper)}
                          className="flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#EBF0F5] text-gray-400 hover:text-[#2B4C7E] transition-all"
                        >
                          <MessageCircle className="h-4 w-4" />
                          <span className="text-xs font-medium">
                            {whisper.comment_count || (whisper.comments || []).length || 0}
                          </span>
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          whisper.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                          whisper.status === 'scheduled' ? 'bg-[#EBF0F5] text-[#1E3A63]' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {whisper.status === 'draft' ? 'Saved' : whisper.status}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(whisper.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Comments Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-lg bg-white rounded-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent">
              💬 Comments
            </DialogTitle>
          </DialogHeader>
          
          {commentWhisper && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Whisper Preview */}
              <div className="bg-[#EBF0F5] rounded-xl p-3 mb-4">
                <h4 className="font-semibold text-gray-800 text-sm">{commentWhisper.title}</h4>
                <p className="text-gray-600 text-xs line-clamp-2">{commentWhisper.content}</p>
              </div>
              
              {/* Comments List */}
              <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[40vh]">
                {loadingComments ? (
                  <div className="text-center py-8 text-gray-400">Loading comments...</div>
                ) : whisperComments.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No comments yet. Be the first!</p>
                  </div>
                ) : (
                  whisperComments.map((comment) => (
                    <div key={comment.comment_id} className="bg-gray-50 rounded-xl p-3 group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-[#1E3A63]">{comment.user_name}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-700 text-sm">{comment.comment}</p>
                        </div>
                        {(comment.user_id === user?.user_id || commentWhisper?.user_id === user?.user_id) && (
                          <button
                            onClick={() => deleteComment(comment.comment_id)}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Comment */}
              <div className="flex gap-2 pt-3 border-t">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 rounded-full"
                  onKeyDown={(e) => e.key === 'Enter' && addComment()}
                />
                <Button
                  onClick={addComment}
                  disabled={!newComment.trim()}
                  className="rounded-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Whisper Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white border border-gray-200 rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center gap-3 pb-4 border-b border-gray-100">
            <div className="w-10 h-10 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-xl flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-800">
              Create New Whisper
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
              <Input
                placeholder="A meaningful title..."
                value={newWhisper.title}
                onChange={(e) => setNewWhisper({ ...newWhisper, title: e.target.value })}
                className="rounded-xl border-gray-200 focus:border-[#3A5C8F] text-base"
                data-testid="whisper-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Recipient</label>
              <Select value={newWhisper.recipient_id} onValueChange={(value) => setNewWhisper({ ...newWhisper, recipient_id: value })}>
                <SelectTrigger className="rounded-xl border-gray-200" data-testid="recipient-select">
                  <SelectValue placeholder="Select recipient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Beneficiaries</SelectItem>
                  {beneficiaries.map((ben) => (
                    <SelectItem key={ben.beneficiary_id} value={ben.beneficiary_id}>
                      {ben.name} {ben.is_primary && '(Primary)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message *</label>
              <Textarea
                placeholder="Write your heartfelt message here..."
                value={newWhisper.content}
                onChange={(e) => setNewWhisper({ ...newWhisper, content: e.target.value })}
                rows={5}
                className="rounded-xl border-gray-200 focus:border-[#3A5C8F] text-base"
                data-testid="whisper-content-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Attach Media (Optional)</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#3A5C8F] transition-colors cursor-pointer bg-gray-50">
                <Input
                  type="file"
                  accept="image/*,video/*,audio/*"
                  onChange={(e) => {
                    setMediaFile(e.target.files[0]);
                    if (e.target.files[0]) {
                      const fileType = e.target.files[0].type.split('/')[0];
                      setNewWhisper({ ...newWhisper, type: fileType });
                      setAlbumAttach(null);
                    }
                  }}
                  className="hidden"
                  id="media-upload"
                  data-testid="media-upload-input"
                />
                <label htmlFor="media-upload" className="cursor-pointer">
                  {mediaFile ? (
                    <div className="flex items-center justify-center gap-2 text-[#2B4C7E]">
                      {getTypeIcon(newWhisper.type)}
                      <span className="font-medium">{mediaFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-[#3A5C8F]" />
                      <p className="font-medium text-gray-700">Click to upload photo, video, or audio</p>
                      <p className="text-xs text-gray-400">Max 10MB</p>
                    </div>
                  )}
                </label>
              </div>

              {/* Attach from a Photo Album */}
              <div className="mt-3" data-testid="album-attach-section">
                {albumAttach ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl border border-[#D7E0EA] bg-[#EBF0F5] px-3 py-2" data-testid="album-attach-chip">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderHeart className="h-4 w-4 text-[#2B4C7E] shrink-0" />
                      <span className="text-sm text-[#2B4C7E] truncate">{albumAttach.label}</span>
                    </div>
                    <button type="button" onClick={() => { setAlbumAttach(null); setBrowsingAlbum(null); }} className="text-[#2B4C7E] hover:text-red-500 shrink-0" data-testid="album-attach-remove">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => { setAlbumPickerOpen(!albumPickerOpen); setBrowsingAlbum(null); }}
                      className="flex items-center gap-2 text-sm font-medium text-[#2B4C7E] hover:underline"
                      data-testid="attach-from-album-btn"
                    >
                      <FolderHeart className="h-4 w-4" /> {albumPickerOpen ? 'Hide albums' : 'Or attach from a Photo Album'}
                    </button>

                    {albumPickerOpen && (
                      <div className="mt-2 border border-gray-200 rounded-xl p-3 bg-white" data-testid="album-picker">
                        {albums.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-3">No albums yet. Create one in Photo Albums first.</p>
                        ) : !browsingAlbum ? (
                          <div className="space-y-2 max-h-56 overflow-y-auto">
                            {albums.map((al) => {
                              const counts = [
                                al.photos?.length ? `${al.photos.length} photo${al.photos.length > 1 ? 's' : ''}` : null,
                                al.videos?.length ? `${al.videos.length} video${al.videos.length > 1 ? 's' : ''}` : null,
                                al.audio_recordings?.length ? `${al.audio_recordings.length} voice` : null,
                              ].filter(Boolean).join(' · ') || 'Empty';
                              return (
                                <div key={al.album_id} className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2" data-testid={`album-row-${al.album_id}`}>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{al.title}</p>
                                    <p className="text-xs text-gray-400">{counts}</p>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <button type="button"
                                      onClick={() => setAlbumAttach({ mode: 'album', album_id: al.album_id, title: al.title, label: `Album: ${al.title}` })}
                                      className="text-xs px-2.5 py-1 rounded-full bg-[#2B4C7E] text-white"
                                      data-testid={`attach-whole-album-${al.album_id}`}
                                      disabled={!al.photos?.length && !al.videos?.length && !al.audio_recordings?.length}
                                    >Send whole album</button>
                                    <button type="button" onClick={() => openBrowse(al)} className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-[#2B4C7E]" data-testid={`browse-album-${al.album_id}`}>Pick photos</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <button type="button" onClick={() => setBrowsingAlbum(null)} className="flex items-center gap-1 text-xs text-gray-500">
                                <ChevronLeft className="h-3 w-3" /> Back to albums
                              </button>
                              <span className="text-xs text-gray-400">Tap items to select</span>
                            </div>
                            <p className="text-sm font-medium text-gray-800 mb-2">{browsingAlbum.title}</p>
                            <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                              {(browsingAlbum.photos || []).map((src, i) => {
                                const sel = pickSel.photos.includes(i);
                                return (
                                  <button key={`p${i}`} type="button" onClick={() => togglePick('photos', i)} className={`relative rounded-lg overflow-hidden border-2 aspect-square ${sel ? 'border-[#2B4C7E]' : 'border-gray-100'}`} data-testid={`album-item-photo-${i}`}>
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                    {sel && <span className="absolute inset-0 bg-[#2B4C7E]/30 flex items-center justify-center"><span className="bg-[#2B4C7E] text-white rounded-full w-5 h-5 flex items-center justify-center"><Check className="h-3 w-3" /></span></span>}
                                  </button>
                                );
                              })}
                              {(browsingAlbum.videos || []).map((src, i) => {
                                const sel = pickSel.videos.includes(i);
                                return (
                                  <button key={`v${i}`} type="button" onClick={() => togglePick('videos', i)} className={`relative rounded-lg overflow-hidden border-2 aspect-square bg-black flex items-center justify-center ${sel ? 'border-[#2B4C7E]' : 'border-gray-100'}`} data-testid={`album-item-video-${i}`}>
                                    <Video className="h-5 w-5 text-white" />
                                    {sel && <span className="absolute top-1 right-1 bg-[#2B4C7E] text-white rounded-full w-5 h-5 flex items-center justify-center"><Check className="h-3 w-3" /></span>}
                                  </button>
                                );
                              })}
                              {(browsingAlbum.audio_recordings || []).map((src, i) => {
                                const sel = pickSel.audio.includes(i);
                                return (
                                  <button key={`a${i}`} type="button" onClick={() => togglePick('audio', i)} className={`relative rounded-lg border-2 aspect-square bg-[#EBF0F5] flex items-center justify-center ${sel ? 'border-[#2B4C7E]' : 'border-gray-100'}`} data-testid={`album-item-audio-${i}`}>
                                    <Mic className="h-5 w-5 text-[#2B4C7E]" />
                                    {sel && <span className="absolute top-1 right-1 bg-[#2B4C7E] text-white rounded-full w-5 h-5 flex items-center justify-center"><Check className="h-3 w-3" /></span>}
                                  </button>
                                );
                              })}
                              {!(browsingAlbum.photos?.length || browsingAlbum.videos?.length || browsingAlbum.audio_recordings?.length) && (
                                <p className="col-span-4 text-sm text-gray-400 text-center py-3">This album is empty.</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={confirmSelection}
                              disabled={pickCount === 0}
                              className="mt-3 w-full text-sm font-semibold py-2 rounded-full bg-[#2B4C7E] text-white disabled:opacity-40"
                              data-testid="confirm-selection-btn"
                            >
                              {pickCount === 0 ? 'Select items to attach' : `Attach ${pickCount} selected`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Frequency</label>
              <Select value={newWhisper.frequency} onValueChange={(value) => setNewWhisper({ ...newWhisper, frequency: value })}>
                <SelectTrigger className="rounded-xl border-gray-200" data-testid="frequency-select">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">One Time (After Passing)</SelectItem>
                  <SelectItem value="weekly">Weekly Reminder</SelectItem>
                  <SelectItem value="monthly">Monthly Reminder</SelectItem>
                  <SelectItem value="yearly">Yearly (Anniversary/Birthday)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Recurring whispers will be sent on the specified frequency after your passing.
              </p>
            </div>

            {newWhisper.frequency && newWhisper.frequency !== 'once' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Deliveries (Optional)</label>
                <Input
                  type="number"
                  placeholder="Leave empty for unlimited"
                  value={newWhisper.max_deliveries}
                  onChange={(e) => setNewWhisper({ ...newWhisper, max_deliveries: e.target.value })}
                  className="rounded-xl"
                  data-testid="max-deliveries-input"
                />
              </div>
            )}

            {/* Family Crest stamp (opt-in) */}
            <Card className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="stamp-crest"
                  checked={newWhisper.stamp_crest}
                  disabled={!crestInfo?.hasRenderedPng}
                  onChange={(e) => setNewWhisper({ ...newWhisper, stamp_crest: e.target.checked })}
                  className="mt-1 h-5 w-5 text-amber-600 rounded border-amber-300 focus:ring-amber-500 disabled:opacity-50"
                  data-testid="stamp-crest-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="stamp-crest" className="font-medium text-gray-800 cursor-pointer flex items-center gap-2">
                    <ShieldPlus className="h-4 w-4 text-amber-600" />
                    Stamp the family crest
                  </label>
                  {crestInfo?.hasRenderedPng ? (
                    <p className="text-xs text-gray-600 mt-1">
                      Adds your saved {crestInfo?.familyName || 'family'} crest to the top of this whisper, its inbox notification, and the delivery email.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600 mt-1">
                      Design &amp; save your family crest first to enable this.{' '}
                      <Link to="/family-tree/crest" className="text-amber-700 underline font-medium" data-testid="stamp-crest-design-link">
                        Open the Crest Studio
                      </Link>
                    </p>
                  )}
                </div>
              </div>
            </Card>

            {/* Send Now Option */}
            <Card className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="send-now"
                  checked={newWhisper.send_now}
                  onChange={(e) => setNewWhisper({ ...newWhisper, send_now: e.target.checked })}
                  className="mt-1 h-5 w-5 text-green-600 rounded border-green-300 focus:ring-green-500"
                  data-testid="send-now-checkbox"
                />
                <div>
                  <label htmlFor="send-now" className="font-medium text-gray-800 cursor-pointer">
                    Send Now (Live Whisper)
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Send this whisper immediately to the recipient. Perfect for sharing memories while you're still here!
                  </p>
                </div>
              </div>

              {newWhisper.send_now && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-green-200"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Method</label>
                  <Select 
                    value={newWhisper.delivery_method} 
                    onValueChange={(value) => setNewWhisper({ ...newWhisper, delivery_method: value })}
                  >
                    <SelectTrigger className="rounded-xl bg-white" data-testid="delivery-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Email + Inbox (Recommended)</SelectItem>
                      <SelectItem value="email">Email Only</SelectItem>
                      <SelectItem value="inbox">Inbox Only</SelectItem>
                    </SelectContent>
                  </Select>
                </motion.div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email preview sensitivity</label>
                <Select
                  value={newWhisper.whisper_sensitivity}
                  onValueChange={(value) => setNewWhisper({ ...newWhisper, whisper_sensitivity: value })}
                >
                  <SelectTrigger className="rounded-xl bg-white" data-testid="whisper-sensitivity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard — show photo/media previews in the email</SelectItem>
                    <SelectItem value="private">Private — mention media, reveal only after secure sign-in</SelectItem>
                    <SelectItem value="highly_sensitive">Highly sensitive — hide all previews, open securely only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-2">
                  Controls how much of any attached photos, video, or voice appears inside the notification email. The full message is always protected behind a secure, single-use link.
                </p>
              </div>
            </Card>

            <Button 
              onClick={createWhisper} 
              disabled={isCreating || !newWhisper.title || !newWhisper.content}
              className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full py-6 hover:from-[#2B4C7E] hover:to-[#B3956B]"
              data-testid="submit-whisper-btn"
            >
              {isCreating ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Creating...
                </motion.div>
              ) : (
                <>
                  <Heart className="h-5 w-5 mr-2" />
                  Create Whisper
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <SimsConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={deleteWhisper}
        title="Delete Whisper?"
        message={`Are you sure you want to delete "${selectedWhisper?.title}"? This message will be permanently removed.`}
        confirmText="Yes, Delete"
        cancelText="Keep It"
        variant="error"
      />
    </div>
  );
}
